/**
 * Hooks réseau de la boutique publique — **tout** appel API du module passe
 * par ici (ARCHITECTURE §9.2 : aucun `fetch` nu dans un composant).
 *
 * Routes consommées (aucune n'exige de session — BOU-10) :
 *
 *   GET    /boutique/accueil?du=&au=&spectacleId=     BOU-01
 *   GET    /boutique/spectacles/:id                   BOU-02
 *   GET    /boutique/representations/:id/plan         BOU-03, REZ-06
 *   GET    /boutique/formules                         BOU-05
 *   GET    /boutique/panier                           BOU-04, REZ-02
 *   POST   /boutique/panier/places                    BOU-04, REZ-01/05
 *   POST   /boutique/panier/places-libres              LIB-03, REZ-05
 *   POST   /boutique/panier/abonnements               BOU-05
 *   PATCH  /boutique/panier/lignes/:id                BOU-09
 *   DELETE /boutique/panier/lignes/:id                BOU-04
 *   POST   /boutique/panier/prolonger                 REZ-03
 *   POST   /boutique/panier/coordonnees               BOU-06
 *   DELETE /boutique/panier                           abandon / accusé d'expiration
 *
 * ## Une règle de fraîcheur propre à la boutique
 *
 * Le back-office tolère 15 secondes de cache sur un plan de salle : deux ou
 * trois personnes écrivent, et un guichetier qui voit une seconde de retard
 * corrige. **Le public, lui, arrive par vagues** — le jour d'une ouverture de
 * vente, une salle change d'état à chaque seconde. Les plans et les jauges sont
 * donc marqués `staleTime: 0` : on préfère une requête de plus à un visiteur
 * qui clique un fauteuil déjà parti.
 *
 * Le refus qui suivrait resterait juste (I-08 tranche en base, pas ici), mais
 * il serait évitable — et un refus évitable est une vente perdue.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import type {
  AccueilBoutique,
  AccueilBoutiqueQuery,
  AjouterAbonnement,
  AjouterPlaces,
  AjouterPlacesLibres,
  CoordonneesAcheteur,
  FormulesBoutique,
  ModifierLignePanier,
  Ok,
  Panier,
  PlanPublic,
  PlanSeriePublic,
  SpectacleDetailBoutique,
} from '@tb/shared'

import { api } from '../../lib/api'

/* -------------------------------------------------------------------------- */
/* Clés de cache                                                              */
/* -------------------------------------------------------------------------- */

export const CLES = {
  racine: ['boutique'] as const,
  accueil: (filtres: AccueilBoutiqueQuery) => ['boutique', 'accueil', filtres] as const,
  spectacle: (id: string) => ['boutique', 'spectacle', id] as const,
  plan: (id: string) => ['boutique', 'plan', id] as const,
  planSerie: (id: string) => ['boutique', 'plan-serie', id] as const,
  formules: ['boutique', 'formules'] as const,
  panier: ['boutique', 'panier'] as const,
} as const

/* -------------------------------------------------------------------------- */
/* Lectures                                                                   */
/* -------------------------------------------------------------------------- */

export function useAccueilBoutique(
  filtres: AccueilBoutiqueQuery = {},
): UseQueryResult<AccueilBoutique> {
  return useQuery({
    queryKey: CLES.accueil(filtres),
    queryFn: ({ signal }) =>
      api.get<AccueilBoutique>('/boutique/accueil', { query: { ...filtres }, signal }),
    /* Les jauges de la page d'accueil bougent en permanence pendant une
       ouverture de vente : « complet » affiché à tort décourage un achat. */
    staleTime: 0,
  })
}

export function useSpectacleBoutique(id: string): UseQueryResult<SpectacleDetailBoutique> {
  return useQuery({
    queryKey: CLES.spectacle(id),
    queryFn: ({ signal }) =>
      api.get<SpectacleDetailBoutique>(`/boutique/spectacles/${id}`, { signal }),
    staleTime: 0,
  })
}

export function usePlanPublic(id: string): UseQueryResult<PlanPublic> {
  return useQuery({
    queryKey: CLES.plan(id),
    queryFn: ({ signal }) =>
      api.get<PlanPublic>(`/boutique/representations/${id}/plan`, { signal }),
    /* ★ Zéro cache, et rafraîchissement au retour d'onglet : c'est l'écran où
       « le siège est pris entre l'affichage et le clic » se produit. On ne peut
       pas l'empêcher — mais on peut réduire la fenêtre à presque rien. */
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function usePlanSeriePublic(id: string): UseQueryResult<PlanSeriePublic> {
  return useQuery({
    queryKey: CLES.planSerie(id),
    queryFn: ({ signal }) => api.get<PlanSeriePublic>(`/boutique/formules/${id}/plan`, { signal }),
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function usePlanDateSeriePublic(
  formuleId: string,
  representationId: string | null,
): UseQueryResult<PlanPublic> {
  return useQuery({
    queryKey: ['boutique', 'plan-serie-date', formuleId, representationId],
    queryFn: ({ signal }) =>
      api.get<PlanPublic>(
        `/boutique/formules/${formuleId}/representations/${representationId}/plan`,
        { signal },
      ),
    enabled: representationId !== null,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function useFormulesBoutique(): UseQueryResult<FormulesBoutique> {
  return useQuery({
    queryKey: CLES.formules,
    queryFn: ({ signal }) => api.get<FormulesBoutique>('/boutique/formules', { signal }),
  })
}

/**
 * Le panier courant — jamais en erreur : « je n'ai rien choisi » et « mon
 * panier a expiré » sont deux réponses normales du serveur (200), pas deux
 * pannes que chaque écran devrait traiter.
 */
export function usePanier(): UseQueryResult<Panier> {
  return useQuery({
    queryKey: CLES.panier,
    queryFn: ({ signal }) => api.get<Panier>('/boutique/panier', { signal }),
    staleTime: 0,
    /* REZ-02 — le compte à rebours est décompté localement par
       `CompteARebours` ; ce rappel périodique resynchronise sur l'horloge du
       SERVEUR et détecte l'expiration même si l'onglet est resté ouvert sans
       interaction. Une minute suffit : le seuil d'avertissement est à deux. */
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

/* -------------------------------------------------------------------------- */
/* Mutations                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Après toute mutation du panier, deux choses changent : le panier lui-même, et
 * la disponibilité de la salle. On invalide donc **les deux**, sans quoi un
 * siège que l'on vient de retenir resterait « libre » sur le plan affiché.
 */
function useInvalidationPanier(): () => Promise<void> {
  const client = useQueryClient()
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: CLES.panier }),
      client.invalidateQueries({ queryKey: ['boutique', 'plan'] }),
      client.invalidateQueries({ queryKey: ['boutique', 'plan-serie'] }),
      client.invalidateQueries({ queryKey: ['boutique', 'accueil'] }),
      client.invalidateQueries({ queryKey: ['boutique', 'spectacle'] }),
    ])
  }
}

export function useAjouterPlaces(): UseMutationResult<Panier, unknown, AjouterPlaces> {
  const invalider = useInvalidationPanier()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (entree: AjouterPlaces) => api.post<Panier>('/boutique/panier/places', entree),
    onSuccess: (panier) => {
      /* Le panier renvoyé fait foi tout de suite : le compte à rebours
         apparaît sans attendre le rafraîchissement. */
      client.setQueryData(CLES.panier, panier)
    },
    onSettled: invalider,
  })
}

/**
 * ★ LIB-03 — retenir N entrées dans un lieu à placement libre.
 *
 * Un hook distinct de `useAjouterPlaces`, comme la route l'est du côté serveur :
 * ici l'on envoie une quantité et le système attribue ; là on désigne des
 * fauteuils. Fondre les deux ferait porter à chaque écran la charge de savoir
 * laquelle des deux moitiés du corps il a le droit de remplir.
 */
export function useAjouterPlacesLibres(): UseMutationResult<Panier, unknown, AjouterPlacesLibres> {
  const invalider = useInvalidationPanier()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (entree: AjouterPlacesLibres) =>
      api.post<Panier>('/boutique/panier/places-libres', entree),
    onSuccess: (panier) => {
      client.setQueryData(CLES.panier, panier)
    },
    onSettled: invalider,
  })
}

export function useAjouterAbonnement(): UseMutationResult<Panier, unknown, AjouterAbonnement> {
  const invalider = useInvalidationPanier()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (entree: AjouterAbonnement) =>
      api.post<Panier>('/boutique/panier/abonnements', entree),
    onSuccess: (panier) => {
      client.setQueryData(CLES.panier, panier)
    },
    // Le panier renvoyé est déjà la vérité. Le plan se rafraîchit en fond ;
    // une lecture lente ne doit pas retarder la navigation après réservation.
    onSettled: () => {
      void invalider()
    },
  })
}

export function useModifierLigne(): UseMutationResult<
  Panier,
  unknown,
  { readonly ligneId: string; readonly patch: ModifierLignePanier }
> {
  const invalider = useInvalidationPanier()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ ligneId, patch }) =>
      api.patch<Panier>(`/boutique/panier/lignes/${ligneId}`, patch),
    onSuccess: (panier) => {
      client.setQueryData(CLES.panier, panier)
    },
    onSettled: invalider,
  })
}

export function useRetirerLigne(): UseMutationResult<Panier, unknown, string> {
  const invalider = useInvalidationPanier()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (ligneId: string) => api.del<Panier>(`/boutique/panier/lignes/${ligneId}`),
    onSuccess: (panier) => {
      client.setQueryData(CLES.panier, panier)
    },
    onSettled: invalider,
  })
}

export function useProlongerPanier(): UseMutationResult<Panier, unknown, void> {
  const invalider = useInvalidationPanier()
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Panier>('/boutique/panier/prolonger'),
    onSuccess: (panier) => {
      client.setQueryData(CLES.panier, panier)
    },
    onSettled: invalider,
  })
}

export function useEnregistrerCoordonnees(): UseMutationResult<
  Panier,
  unknown,
  CoordonneesAcheteur
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (entree: CoordonneesAcheteur) =>
      api.post<Panier>('/boutique/panier/coordonnees', entree),
    onSuccess: (panier) => {
      client.setQueryData(CLES.panier, panier)
    },
  })
}

/**
 * Abandon — et **accusé de réception d'une expiration**.
 *
 * Le même appel sert aux deux : le serveur efface le cookie, le panier suivant
 * repartira de zéro. Sans ce geste explicite, le message « votre panier a
 * expiré » réapparaîtrait à chaque écran, indéfiniment.
 */
export function useAbandonnerPanier(): UseMutationResult<Ok, unknown, void> {
  const invalider = useInvalidationPanier()
  return useMutation({
    mutationFn: () => api.del<Ok>('/boutique/panier'),
    onSettled: invalider,
  })
}
